import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import Skeleton from '@material-ui/lab/Skeleton';
import {Button, Header, Section as UESection} from 'epic-ue-components';
import {PageData, SectionProps, UELHeaderProps} from 'epic-ue-shared';
import Select from './components/Select';
import Error404 from '../error/Error404';
import {SdkResult} from '../../sdk/sdk.types';
import {useSdk} from '../../sdk/sdk.hooks';
import {getHelmetForMetaTags} from '../../utils/epicGamesUtils';
import {useLoading, useNotFound} from '../../utils/loadingUtils';
import MainSection, {MainSectionProps} from './components/MainSection';
import LoadIndicator from '../../components/common/loading/LoadIndicator';
import SuccessSection from './components/SuccessSection';
import NewsletterSection, {NewsletterSectionProps} from './components/NewsletterSection';
import root from 'window-or-global';

interface SdkPageData extends PageData {
    header: UELHeaderProps;
    logosSection: SectionProps;
    mainSection: MainSectionProps;
    successSection: SectionProps;
    newsletter: NewsletterSectionProps;
}

interface Props {
    pageData: SdkPageData;
}

const Section = styled.section`
    background-color: #111111;
    .header {
        .title {
            color: #e6e6ea;
            font-size: 56px;
            font-weight: 700;
            line-height: 65px;
            font-family: Inter;
            margin-bottom: 16px;
            letter-spacing: -0.2px;
        }
        .subtitle {
            color: #e6e6ea;
            font-size: 30px;
            font-weight: 400;
            line-height: 36px;
            font-family: Inter;
            letter-spacing: -0.3px;
        }

        ${p => p.theme.breakpoints.down('md')} {
            padding: 0 16px;
            overflow: hidden;

            .title {
                font-size: 48px;
                margin-bottom: 16px;
                letter-spacing: -0.2px;
            }
            .subtitle {
                font-size: 24px;
                letter-spacing: -0.3px;
            }
        }
    }
`;

const StyledForm = styled.form`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 16px;

    ${p => p.theme.breakpoints.down('xs')} {
        flex-direction: column;
    }
`;

const StyledSkeleton = styled(Skeleton)`
    width: 100%;
    border-radius: 8px;
`;

const Sdk = ({pageData}: Props) => {
    const {data: sdkRes, isLoading: typesLoading} = useSdk();
    const [selectedType, setSelectedType] = useState<string>('');
    const [selectedVersion, setSelectedVersion] = useState<number>();

    const isLoading = useLoading(pageData);
    const notFound = useNotFound(pageData);

    const sdkResults = sdkRes?.results || {};
    const results: SdkResult[] = Object.values(sdkResults).reduce((acc, sdk) => (sdk && !sdk.error ? [...acc, sdk] : acc), []);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newType = e.target.value;
            setSelectedType(newType);

            const type = results.find(r => r.archive_type === newType) || {};
            console.info('Latest version', type.version, 'should be selected for:', newType);

            setSelectedVersion(type.archive_id);
        },
        [results, setSelectedType, setSelectedVersion]
    );

    if (isLoading) {
        return <LoadIndicator fill />;
    }
    if (notFound) {
        return <Error404 />;
    }

    return (
        <Section>
            {getHelmetForMetaTags(pageData)}
            <Header {...pageData?.header} />
            <UESection {...pageData?.logosSection} />
            <MainSection
                {...pageData?.mainSection}
                render={({inputLabel, buttonLabel}) => (
                    <StyledForm>
                        {typesLoading ? (
                            <StyledSkeleton variant="rect" height="56px" />
                        ) : (
                            <Select
                                name="sdkType"
                                label={inputLabel}
                                value={selectedType}
                                onChange={handleChange}
                                options={results.map(({archive_type: value, typeLabel: label}) => ({value, label}))}
                            />
                        )}
                        <Button
                            disabled={typesLoading || !selectedType || !selectedVersion}
                            href={`${root.clientEnvConfig.EPIC_SERVER_URL || ''}/api/sdk/download?archive_id=${selectedVersion}&archive_type=${selectedType}`}
                            target="_blank">
                            {buttonLabel}
                        </Button>
                    </StyledForm>
                )}
            />
            <SuccessSection {...pageData?.successSection} />
            <NewsletterSection {...pageData?.newsletter} />
        </Section>
    );
};

export default Sdk;
